import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const ComplianceFitFinder = () => {
  const [currentStep, setCurrentStep] = useState('landing');
  const [email, setEmail] = useState('');
  const [responses, setResponses] = useState({});
  const [recommendations, setRecommendations] = useState([]);

  // Certification database with scoring logic
  const certifications = {
    'SOC2': {
      name: 'SOC 2 Type II',
      description: 'Security & Availability Controls',
      industryRelevance: { 'SaaS': 95, 'FinTech': 90, 'HealthTech': 85, 'E-commerce': 75, 'EdTech': 80, 'Cybersecurity': 85 },
      customerMatch: { 'Enterprise': 95, 'SMB': 60, 'Government': 80, 'Healthcare': 85, 'Financial': 90 },
      geoRelevance: { 'US': 100, 'Canada': 90, 'EU': 70, 'Global': 85 },
      dataTypes: { 'PII': 90, 'Payment': 85, 'Health': 80, 'Financial': 85, 'Business': 95 },
      timelineMonths: '3–6',
      costRange: '$7K–$50K',
      implementationGuideUrl: 'https://sprinto.com/blog/soc-2-audit/',
      blueprintUrl: 'https://blueprint.sprinto.com/soc2/signup?utm_source=fitfinder&utm_medium=referral&utm_campaign=fit_finder_launch',
      implementation: [
        'Define the scope',
        'Conduct a gap analysis',
        'Conduct risk assessment',
        'Implement controls',
        'Undergo readiness assessment',
        'Complete the audit'
      ],
      triggers: ['Enterprise', 'SaaS', 'PII', 'Previous_blocks']
    },
    'ISO27001': {
      name: 'ISO 27001',
      description: 'Information Security Management',
      industryRelevance: { 'FinTech': 100, 'Cybersecurity': 95, 'SaaS': 85, 'HealthTech': 80, 'E-commerce': 70 },
      customerMatch: { 'Enterprise': 100, 'Government': 90, 'Financial': 100, 'Healthcare': 85, 'SMB': 50 },
      geoRelevance: { 'EU': 100, 'Global': 95, 'US': 75, 'Canada': 80 },
      dataTypes: { 'Financial': 100, 'Business': 90, 'PII': 80, 'Payment': 85 },
      timelineMonths: '4–8',
      costRange: '$10K–$60K',
      implementationGuideUrl: 'https://sprinto.com/blog/iso-27001-checklist/',
      implementation: [
        'Define ISMS scope',
        'Perform risk assessment',
        'Implement controls and policies',
        'Conduct internal audit',
        'Undergo certification audit'
      ],
      triggers: ['EU', 'Financial', 'Global', 'Enterprise']
    },
    'GDPR': {
      name: 'GDPR Compliance',
      description: 'EU Data Protection Regulation',
      industryRelevance: { 'SaaS': 80, 'E-commerce': 85, 'FinTech': 90, 'HealthTech': 85, 'EdTech': 80 },
      customerMatch: { 'EU': 100, 'Global': 90, 'Enterprise': 85, 'SMB': 80 },
      geoRelevance: { 'EU': 100, 'Global': 90, 'US': 30, 'Canada': 40 },
      dataTypes: { 'PII': 100, 'Health': 95, 'Financial': 90, 'Business': 85 },
      timelineMonths: '2–4',
      costRange: '$5K–$25K',
      implementationGuideUrl: 'https://sprinto.com/blog/gdpr-compliance/',
      implementation: [
        'Appoint a Data Protection Officer (DPO)',
        'Map and classify data',
        'Update privacy policies',
        'Implement data subject rights processes',
        'Conduct DPIA (Data Protection Impact Assessment)',
        'Train staff and monitor compliance'
      ],
      triggers: ['EU', 'PII', 'Global']
    },
    'HIPAA': {
      name: 'HIPAA Compliance',
      description: 'Healthcare Data Protection',
      industryRelevance: { 'HealthTech': 100, 'SaaS': 30, 'FinTech': 20, 'E-commerce': 15 },
      customerMatch: { 'Healthcare': 100, 'Enterprise': 40, 'Government': 60 },
      geoRelevance: { 'US': 100, 'Canada': 30, 'EU': 20, 'Global': 50 },
      dataTypes: { 'Health': 100, 'PII': 60, 'Business': 30 },
      timelineMonths: '3–6',
      costRange: '$4K–$20K',
      implementationGuideUrl: 'https://sprinto.com/get-hipaa/',
      implementation: [
        'Conduct a HIPAA risk assessment',
        'Develop and implement policies and procedures',
        'Train workforce',
        'Implement technical and physical safeguards',
        'Conduct regular audits and reviews'
      ],
      triggers: ['HealthTech', 'Healthcare', 'Health']
    },
    'PCI_DSS': {
      name: 'PCI DSS',
      description: 'Payment Card Industry Security',
      industryRelevance: { 'E-commerce': 100, 'FinTech': 95, 'SaaS': 40, 'HealthTech': 30 },
      customerMatch: { 'Enterprise': 80, 'SMB': 70, 'Financial': 90, 'Healthcare': 60 },
      geoRelevance: { 'US': 100, 'EU': 100, 'Canada': 100, 'Global': 100 },
      dataTypes: { 'Payment': 100, 'Financial': 80, 'PII': 40 },
      timelineMonths: '2–4',
      costRange: '$5K–$25K',
      implementationGuideUrl: 'https://sprinto.com/blog/pci-dss-compliance/',
      implementation: [
        'Determine PCI DSS level',
        'Complete self-assessment questionnaire (SAQ)',
        'Remediate gaps',
        'Conduct external scan (ASV)',
        'Submit compliance report'
      ],
      triggers: ['Payment', 'E-commerce', 'Financial']
    },
    'FedRAMP': {
      name: 'FedRAMP',
      description: 'Federal Risk Authorization',
      industryRelevance: { 'SaaS': 60, 'Cybersecurity': 80, 'FinTech': 50, 'HealthTech': 55 },
      customerMatch: { 'Government': 100, 'Enterprise': 30, 'SMB': 10 },
      geoRelevance: { 'US': 100, 'Canada': 20, 'EU': 10, 'Global': 40 },
      dataTypes: { 'Government': 100, 'Business': 70, 'PII': 80 },
      timelineMonths: '6–12',
      costRange: '$200K–$500K',
      implementationGuideUrl: 'https://sprinto.com/blog/fedramp-compliance/',
      implementation: [
        'Select a FedRAMP authorized 3PAO',
        'Conduct readiness assessment',
        'Remediate findings',
        'Complete full security assessment',
        'Submit for FedRAMP authorization'
      ],
      triggers: ['Government', 'US']
    }
  };

  // Questions configuration
  const questions = [
    {
      id: 'industry',
      title: 'What industry are you in?',
      type: 'single',
      required: true,
      options: [
        { value: 'SaaS', label: 'SaaS/Cloud Software' },
        { value: 'FinTech', label: 'Financial Technology' },
        { value: 'HealthTech', label: 'Healthcare Technology' },
        { value: 'E-commerce', label: 'E-commerce/Retail' },
        { value: 'EdTech', label: 'Education Technology' },
        { value: 'Cybersecurity', label: 'Cybersecurity' },
        { value: 'Other', label: 'Other' }
      ]
    },
    {
      id: 'customers',
      title: 'Who do you sell to?',
      type: 'multiple',
      required: true,
      options: [
        { value: 'SMB', label: 'Small businesses' },
        { value: 'Enterprise', label: 'Large enterprises' },
        { value: 'Government', label: 'Government agencies' },
        { value: 'Healthcare', label: 'Healthcare organizations' },
        { value: 'Financial', label: 'Financial institutions' },
        { value: 'EU', label: 'European customers' }
      ]
    },
    {
      id: 'geography',
      title: 'Where do you operate?',
      type: 'single',
      required: true,
      options: [
        { value: 'US', label: 'United States only' },
        { value: 'Canada', label: 'US and Canada' },
        { value: 'EU', label: 'European Union' },
        { value: 'Global', label: 'Multiple regions' }
      ]
    },
    {
      id: 'dataTypes',
      title: 'What data do you handle?',
      type: 'multiple',
      required: true,
      options: [
        { value: 'PII', label: 'Personal information' },
        { value: 'Payment', label: 'Payment information' },
        { value: 'Health', label: 'Health information' },
        { value: 'Financial', label: 'Financial records' },
        { value: 'Business', label: 'Business intelligence' },
        { value: 'Auth', label: 'Authentication data' },
        { value: 'None', label: 'No sensitive data' }
      ]
    },
    {
      id: 'revenue',
      title: 'What stage are you at?',
      type: 'single',
      required: true,
      showIf: (responses) => responses.customers?.includes('Enterprise'),
      options: [
        { value: 'Pre-revenue', label: 'Pre-revenue' },
        { value: '100K-1M', label: '$100K - $1M ARR' },
        { value: '1M-5M', label: '$1M - $5M ARR' },
        { value: '5M+', label: '$5M+ ARR' }
      ]
    },
    {
      id: 'blockers',
      title: 'Any compliance challenges?',
      type: 'single',
      required: false,
      showIf: (responses) => responses.customers?.includes('Enterprise') || responses.customers?.includes('Government'),
      options: [
        { value: 'Lost_deals', label: 'Lost deals due to missing certifications' },
        { value: 'Delayed_deals', label: 'Deals delayed in security reviews' },
        { value: 'Questionnaires', label: 'Can\'t answer security questionnaires' },
        { value: 'Prospects_asking', label: 'Prospects asking about certifications' },
        { value: 'None', label: 'No challenges yet' }
      ]
    }
  ];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Calculate recommendation score
  const calculateScore = (cert, userResponses) => {
    let score = 0;
    const certData = certifications[cert];

    // Industry relevance (40% weight)
    const industry = userResponses.industry;
    if (industry && certData.industryRelevance[industry]) {
      score += certData.industryRelevance[industry] * 0.4;
    }

    // Customer match (30% weight)
    const customers = userResponses.customers || [];
    let customerScore = 0;
    customers.forEach(customer => {
      if (certData.customerMatch[customer]) {
        customerScore = Math.max(customerScore, certData.customerMatch[customer]);
      }
    });
    score += customerScore * 0.3;

    // Geographic relevance (20% weight)
    const geo = userResponses.geography;
    if (geo && certData.geoRelevance[geo]) {
      score += certData.geoRelevance[geo] * 0.2;
    }

    // Data types (10% weight)
    const dataTypes = userResponses.dataTypes || [];
    let dataScore = 0;
    dataTypes.forEach(dataType => {
      if (certData.dataTypes[dataType]) {
        dataScore = Math.max(dataScore, certData.dataTypes[dataType]);
      }
    });
    score += dataScore * 0.1;

    // Apply boosters
    if (userResponses.blockers === 'Lost_deals') score += 20;
    if (userResponses.blockers === 'Delayed_deals') score += 15;
    if (customers.includes('Financial') && cert === 'ISO27001') score += 25;
    if (customers.includes('EU') && cert === 'GDPR') score += 30;
    if (dataTypes.includes('Health') && cert === 'HIPAA') score += 30;

    return Math.min(score, 100);
  };

  // Generate recommendations
  const generateRecommendations = (userResponses) => {
    const recs = [];

    Object.keys(certifications).forEach(certKey => {
      const score = calculateScore(certKey, userResponses);
      const certData = certifications[certKey];

      if (score >= 50) {
        let priority = 'Consider';
        if (score >= 90) priority = 'Critical';
        else if (score >= 75) priority = 'High';

        const reasoning = generateReasoning(certKey, userResponses, score);

        recs.push({
          certification: certKey,
          ...certData,
          priority,
          score: Math.round(score),
          reasoning
        });
      }
    });

    return recs.sort((a, b) => b.score - a.score);
  };

  // Generate personalized reasoning
  const generateReasoning = (certKey, userResponses, score) => {
    const reasons = [];
    const industry = userResponses.industry;
    const customers = userResponses.customers || [];
    const dataTypes = userResponses.dataTypes || [];
    const geography = userResponses.geography;
    const blockers = userResponses.blockers;
    
    if (certKey === 'SOC2') {
      if (industry === 'SaaS') {
        reasons.push('93% of enterprise SaaS buyers require SOC 2 Type II before signing contracts over $100K');
        reasons.push('Reduces your sales cycle by an average of 45% when targeting enterprise customers');
      }
      if (customers.includes('Enterprise')) {
        reasons.push('Enterprise procurement teams use SOC 2 as a standard vendor qualification requirement');
        reasons.push('Enables you to complete security questionnaires that currently block your deals');
      }
      if (blockers === 'Lost_deals') {
        reasons.push('Directly addresses the compliance gap that\'s costing you enterprise deals');
      }
      if (dataTypes.includes('PII')) {
        reasons.push('Demonstrates proper controls for personal data handling that enterprise customers require');
      }
    }
    
    if (certKey === 'ISO27001') {
      if (customers.includes('Financial')) {
        reasons.push('Required by 89% of financial institutions for third-party vendor relationships');
        reasons.push('Globally recognized standard that opens doors to international financial services');
      }
      if (geography === 'EU' || geography === 'Global') {
        reasons.push('Preferred certification in European markets - often required for government contracts');
        reasons.push('Demonstrates comprehensive security management that European regulators expect');
      }
      if (customers.includes('Enterprise')) {
        reasons.push('Shows enterprise-grade security maturity beyond basic compliance');
        reasons.push('Positions you as a strategic partner rather than just a vendor');
      }
    }
    
    if (certKey === 'GDPR') {
      if (geography === 'EU' || customers.includes('EU')) {
        reasons.push('Legally required for processing EU personal data - non-compliance fines up to 4% of revenue');
        reasons.push('Enables expansion into the €4.2 trillion EU digital market');
      }
      if (dataTypes.includes('PII')) {
        reasons.push('Establishes privacy-by-design practices that differentiate you from competitors');
        reasons.push('Builds customer trust through transparent data handling practices');
      }
      if (industry === 'SaaS' || industry === 'E-commerce') {
        reasons.push('Increasingly expected by US customers who value privacy protection');
      }
    }
    
    if (certKey === 'HIPAA') {
      if (industry === 'HealthTech') {
        reasons.push('Mandatory for any healthcare technology handling patient data in the US');
        reasons.push('Unlocks the $350B+ US healthcare technology market');
      }
      if (customers.includes('Healthcare')) {
        reasons.push('Required by healthcare organizations before they can share patient data with vendors');
        reasons.push('Enables partnerships with hospitals, clinics, and health systems');
      }
      if (dataTypes.includes('Health')) {
        reasons.push('Legal requirement - non-compliance can result in fines up to $1.5M per incident');
      }
    }
    
    if (certKey === 'PCI_DSS') {
      if (dataTypes.includes('Payment')) {
        reasons.push('Legally required for any business processing credit card payments');
        reasons.push('Reduces your liability in case of a data breach involving payment information');
      }
      if (industry === 'E-commerce') {
        reasons.push('Required by payment processors - non-compliance can result in account termination');
        reasons.push('Reduces transaction fees through compliant merchant status');
      }
    }
    
    if (certKey === 'FedRAMP') {
      if (customers.includes('Government')) {
        reasons.push('Required for selling to US federal agencies - a $600B+ annual market');
        reasons.push('Only ~300 cloud services have FedRAMP authorization, creating significant competitive advantage');
      }
      if (geography === 'US') {
        reasons.push('Opens opportunities with state and local governments who often follow federal standards');
      }
    }

    // Fallback reasons if specific conditions aren't met
    if (reasons.length === 0) {
      if (customers.includes('Enterprise')) {
        reasons.push('Addresses common enterprise security requirements for vendor relationships');
        reasons.push('Demonstrates security maturity that enterprise buyers expect');
      } else {
        reasons.push('Positions your company for future enterprise opportunities');
        reasons.push('Establishes security practices that scale with your business growth');
      }
    }

    return reasons;
  };

  // Handle form submission
  const handleEmailSubmit = () => {
    if (email && email.includes('@')) {
      setCurrentStep('questionnaire');
    }
  };

  // Handle question response
  const handleQuestionResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Get current question
  const getCurrentQuestion = () => {
    const visibleQuestions = questions.filter(q => 
      !q.showIf || q.showIf(responses)
    );
    return visibleQuestions[currentQuestionIndex];
  };

  // Navigate questions
  const nextQuestion = () => {
    const visibleQuestions = questions.filter(q => 
      !q.showIf || q.showIf(responses)
    );
    
    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const recs = generateRecommendations(responses);
      setRecommendations(recs);
      setCurrentStep('results');
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Get progress percentage
  const getProgress = () => {
    const visibleQuestions = questions.filter(q => 
      !q.showIf || q.showIf(responses)
    );
    return ((currentQuestionIndex + 1) / visibleQuestions.length) * 100;
  };

  // Landing page
  if (currentStep === 'landing') {
    return (
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-light text-gray-900 mb-4 tracking-tight">
                Find your Compliance
              </h1>
              <p className="text-gray-600 font-light leading-relaxed">
                Find the right certifications for your startup in 3 minutes
              </p>
            </div>

            <div className="space-y-4 mb-12">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-0 py-4 text-lg bg-transparent border-0 border-b border-gray-200 focus:border-gray-900 focus:outline-none transition-colors placeholder-gray-400"
              />
              <button
                onClick={handleEmailSubmit}
                disabled={!email || !email.includes('@')}
                className="w-full py-4 bg-gray-900 text-white font-light text-lg hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
              >
                Begin
              </button>
            </div>

            <div className="text-center text-sm text-gray-400 font-light">
              No spam. Results delivered instantly.
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Questionnaire
  if (currentStep === 'questionnaire') {
    const currentQuestion = getCurrentQuestion();
    const visibleQuestions = questions.filter(q => !q.showIf || q.showIf(responses));
    const isLastQuestion = currentQuestionIndex === visibleQuestions.length - 1;
    const canProceed = responses[currentQuestion?.id];

    return (
      <div className="flex flex-col min-h-screen bg-white">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-lg w-full">
            {/* Progress */}
            <div className="mb-16">
              <div className="h-px bg-gray-100 w-full mb-4">
                <div 
                  className="h-px bg-gray-900 transition-all duration-700 ease-out"
                  style={{ width: `${getProgress()}%` }}
                />
              </div>
              <div className="flex justify-between text-sm text-gray-400 font-light">
                <span>{currentQuestionIndex + 1} of {visibleQuestions.length}</span>
                <span>{Math.round(getProgress())}%</span>
              </div>
            </div>

            {/* Question */}
            <div className="mb-16">
              <h2 className="text-3xl font-light text-gray-900 mb-12 tracking-tight leading-tight">
                {currentQuestion?.title}
              </h2>

              <div className={`grid gap-3 ${currentQuestion?.options.length > 5 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                {currentQuestion?.options.map((option) => (
                  <label
                    key={option.value}
                    className="block cursor-pointer group"
                  >
                    <div className="flex items-center py-4 px-6 border border-gray-100 hover:border-gray-200 transition-colors">
                      <input
                        type={currentQuestion.type === 'multiple' ? 'checkbox' : 'radio'}
                        name={currentQuestion.id}
                        value={option.value}
                        checked={
                          currentQuestion.type === 'multiple'
                            ? responses[currentQuestion.id]?.includes(option.value) || false
                            : responses[currentQuestion.id] === option.value
                        }
                        onChange={(e) => {
                          if (currentQuestion.type === 'multiple') {
                            const current = responses[currentQuestion.id] || [];
                            if (e.target.checked) {
                              handleQuestionResponse(currentQuestion.id, [...current, option.value]);
                            } else {
                              handleQuestionResponse(currentQuestion.id, current.filter(v => v !== option.value));
                            }
                          } else {
                            handleQuestionResponse(currentQuestion.id, option.value);
                          }
                        }}
                        className="sr-only"
                      />
                      <div className="w-5 h-5 border border-gray-300 mr-4 flex-shrink-0 flex items-center justify-center">
                        {((currentQuestion.type === 'multiple' && responses[currentQuestion.id]?.includes(option.value)) ||
                          (currentQuestion.type === 'single' && responses[currentQuestion.id] === option.value)) && (
                          <Check className="w-3 h-3 text-gray-900" />
                        )}
                      </div>
                      <span className="text-gray-900 font-light">
                        {option.label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex items-center text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-light"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              
              <button
                onClick={nextQuestion}
                disabled={!canProceed}
                className="flex items-center text-gray-900 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-light"
              >
                {isLastQuestion ? 'Finish' : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Results page
  if (currentStep === 'results') {
    const getPriorityStyle = (priority) => {
      switch (priority) {
        case 'Critical':
        case 'High':
          return 'bg-red-600 text-white';
        default: return 'bg-gray-200 text-gray-700';
      }
    };

    return (
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header showExpertButton={true} title="Your recommendations" />
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto px-2 md:px-6">
            {/* Header */}
            <div className="text-center mb-12 pt-2">
              {/* Removed duplicate <h1> header */}
            </div>

            {/* Recommendations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              {recommendations.slice(0, 3).map((rec, index) => (
                <div key={rec.certification} className="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
                  <div className="flex justify-between items-start mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h3 className="text-2xl font-semibold text-gray-900">
                          {rec.name}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityStyle(rec.priority)}`}>
                          {rec.priority}
                        </span>
                      </div>
                      <p className="text-gray-600 font-light mb-6">
                        {rec.description}
                      </p>
                    </div>
                    
                    <div className="text-right ml-8">
                      <div className="text-3xl font-light text-gray-900 mb-1">
                        {rec.score}%
                      </div>
                      <div className="text-sm text-gray-400 font-light">
                        Match Score
                      </div>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-gray-900 mb-4 font-light text-lg">
                      Why this matters for your business:
                    </h4>
                    <ul className="space-y-3">
                      {rec.reasoning.map((reason, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2.5 mr-4 flex-shrink-0"></span>
                          <span className="text-gray-600 font-light leading-relaxed">
                            {reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Implementation Steps */}
                  <div className="mb-8">
                    <h4 className="text-gray-900 mb-4 font-light text-lg">
                      Typical Implementation Steps:
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-600 font-light">
                      {rec.implementation && rec.implementation.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ol>
                    <div className="mt-4">
                      {rec.certification === 'SOC2' && rec.blueprintUrl ? (
                        <a 
                          href={rec.blueprintUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-light hover:bg-gray-800 transition-colors"
                        >
                          Get SOC 2 Blueprint
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                      ) : (
                        <a 
                          href={rec.implementationGuideUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-gray-600 hover:text-gray-900 font-light text-sm transition-colors"
                        >
                          View detailed implementation guide
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-12">
                    <div>
                      <span className="text-gray-400 font-light text-sm">Timeline</span>
                      <p className="text-gray-900 font-light text-lg">{rec.timelineMonths}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 font-light text-sm">Investment</span>
                      <p className="text-gray-900 font-light text-lg">{rec.costRange}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mb-16">
              <div className="bg-white rounded-lg border border-gray-200 p-12">
                <h3 className="text-2xl font-light text-gray-900 mb-4">
                  Ready to get started?
                </h3>
                <p className="text-gray-600 font-light mb-8 max-w-md mx-auto">
                  Book a free 30-minute consultation to discuss your compliance roadmap and next steps
                </p>
                <button className="px-8 py-4 bg-gray-900 text-white font-light text-lg hover:bg-gray-800 transition-colors">
                  Talk to an expert
                </button>
              </div>
            </div>

            {/* Reset */}
            <div className="text-center">
              <button
                onClick={() => {
                  setCurrentStep('landing');
                  setEmail('');
                  setResponses({});
                  setCurrentQuestionIndex(0);
                  setRecommendations([]);
                }}
                className="text-gray-400 hover:text-gray-900 font-light text-sm transition-colors"
              >
                Start over
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return null;
};

export default ComplianceFitFinder; 